import { useEffect, useState } from 'react';
import { useToken } from '../auth/useToken';
import { getStatus, startLink } from '../lib/chronos';
import { Icon } from './Icon';

export function ChronosBanner() {
  const getToken = useToken();
  const [visible, setVisible] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (dismissed) return;
    (async () => {
      try {
        const token = await getToken();
        const status = await getStatus(token);
        setVisible(status.enabled && !status.linked);
        setReconnect(status.needsReconnect === true);
      } catch {
        // silently skip — banner is non-critical
      }
    })();
  }, [getToken, dismissed]);

  if (!visible || dismissed) return null;

  // Same start→redirect flow for both "link" and "reconnect" — the difference
  // is purely copy. A broken link comes back as { linked: false,
  // needsReconnect: true }; an expired token expired silently, so this banner is
  // how the user finds out and one-click recovers.
  const link = async () => {
    setBusy(true);
    try {
      const token = await getToken();
      const { authorizeUrl } = await startLink(token);
      window.location.href = authorizeUrl;
    } catch (err) {
      console.error('Chronos link failed:', err);
      setBusy(false);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    setVisible(false);
  };

  const title = reconnect ? 'Reconnect Chronos' : 'Connect Chronos';
  const message = reconnect
    ? 'Your Chronos connection expired. Reconnect to keep asking about time entries, projects, and dashboards.'
    : 'Link your account to ask about time entries, projects, and dashboards.';

  return (
    <div className="chronos-banner" role="region" aria-label={title}>
      <span className="chronos-banner__icon" aria-hidden="true">
        <Icon name="i-clock" size={18} />
      </span>
      <div className="chronos-banner__body">
        <div className="chronos-banner__title">{title}</div>
        <div className="chronos-banner__msg">{message}</div>
      </div>
      <div className="chronos-banner__actions">
        <button
          type="button"
          onClick={link}
          disabled={busy}
          className="chronos-banner__cta"
        >
          {busy
            ? reconnect ? 'Reconnecting…' : 'Linking…'
            : reconnect ? 'Reconnect' : 'Link'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="chronos-banner__close"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
